import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { racesAPI, tidbitsAPI } from '../../services/api';
import * as XLSX from 'xlsx';
import '../../App.css';

const RATING_CATEGORIES = [
  { key: 'B',   label: 'Breeding' },
  { key: 'H',   label: 'Hype' },
  { key: 'N',   label: 'Name' },
  { key: 'M',   label: 'Mystery' },
  { key: 'K',   label: 'Karma' },
  { key: 'A',   label: 'Annoyance' },
  { key: 'Hnd', label: 'Handicapping' },
];

const DEFAULT_RATINGS = () => Object.fromEntries([...RATING_CATEGORIES.map(c => [c.key, '']), ['total', '']]);

// ---- CSV / Excel helpers ----

/** Convert a races array to flat rows (one row per horse, race columns repeated). */
const horsesToFlatRows = (races) => {
  const rows = [];
  races.forEach(race => {
    const raceBase = {
      raceId:       race.raceId || '',
      raceName:     race.raceName || '',
      venue:        race.venue || '',
      location:     race.location || '',
      raceDate:     race.raceDate || '',
      pickDeadline: race.pickDeadline || '',
      raceType:     race.raceType || '',
      distance:     race.distance || '',
      status:       race.status || '',
    };
    const horses = race.horses || [];
    if (horses.length === 0) {
      rows.push({ ...raceBase, horseId: '', horseName: '', postPosition: '', jockey: '', trainer: '',
        odds_decimal: '', odds_fractional: '', odds_american: '', scratched: '',
        rating_B: '', rating_H: '', rating_N: '', rating_M: '', rating_K: '', rating_A: '', rating_Hnd: '', rating_total: '' });
    } else {
      horses.forEach(horse => {
        rows.push({
          ...raceBase,
          horseId:        horse.horseId || '',
          horseName:      horse.horseName || '',
          postPosition:   horse.postPosition ?? '',
          jockey:         horse.jockey || '',
          trainer:        horse.trainer || '',
          odds_decimal:   horse.odds?.decimal ?? '',
          odds_fractional: horse.odds?.fractional ?? '',
          odds_american:  horse.odds?.american ?? '',
          scratched:      horse.scratched ? 'true' : 'false',
          rating_B:       horse.ratings?.B ?? '',
          rating_H:       horse.ratings?.H ?? '',
          rating_N:       horse.ratings?.N ?? '',
          rating_M:       horse.ratings?.M ?? '',
          rating_K:       horse.ratings?.K ?? '',
          rating_A:       horse.ratings?.A ?? '',
          rating_Hnd:     horse.ratings?.Hnd ?? '',
          rating_total:   horse.ratings?.total ?? '',
        });
      });
    }
  });
  return rows;
};

/** Reconstruct races from flat CSV rows (inverse of horsesToFlatRows). */
const flatRowsToRaces = (rows) => {
  const racesMap = new Map();
  rows.forEach(row => {
    const key = row.raceId || `${row.raceName}|${row.venue}`;
    if (!key || key === '|') return;
    if (!racesMap.has(key)) {
      racesMap.set(key, {
        ...(row.raceId ? { raceId: row.raceId } : {}),
        raceName:     row.raceName,
        venue:        row.venue,
        location:     row.location,
        raceDate:     row.raceDate,
        pickDeadline: row.pickDeadline,
        raceType:     row.raceType || 'thoroughbred',
        distance:     row.distance || '',
        status:       row.status || 'upcoming',
        horses:       [],
      });
    }
    if (row.horseName) {
      const race = racesMap.get(key);
      const parseRating = (v) => (v !== undefined && v !== '') ? Number(v) : '';
      race.horses.push({
        ...(row.horseId ? { horseId: row.horseId } : {}),
        horseName:    row.horseName,
        postPosition: parseInt(row.postPosition) || 0,
        jockey:       row.jockey || '',
        trainer:      row.trainer || '',
        odds: {
          decimal:    row.odds_decimal || '',
          fractional: row.odds_fractional || '',
          american:   row.odds_american || '',
        },
        scratched: row.scratched === 'true' || row.scratched === true,
        ratings: {
          B:   parseRating(row.rating_B),
          H:   parseRating(row.rating_H),
          N:   parseRating(row.rating_N),
          M:   parseRating(row.rating_M),
          K:   parseRating(row.rating_K),
          A:   parseRating(row.rating_A),
          Hnd: parseRating(row.rating_Hnd),
          total: parseRating(row.rating_total),
        },
      });
    }
  });
  return Array.from(racesMap.values());
};

/** Parse horse rows from the Horses sheet of an Excel workbook. */
const horseRowsToHorses = (horseRows) => {
  const parseRating = (v) => (v !== undefined && v !== '') ? Number(v) : '';
  return horseRows.map(h => ({
    ...(h.horseId ? { horseId: h.horseId } : {}),
    horseName:    h.horseName || '',
    postPosition: parseInt(h.postPosition) || 0,
    jockey:       h.jockey || '',
    trainer:      h.trainer || '',
    odds: {
      decimal:    h.odds_decimal || '',
      fractional: h.odds_fractional || '',
      american:   h.odds_american || '',
    },
    scratched: h.scratched === 'true' || h.scratched === true,
    ratings: {
      B:   parseRating(h.rating_B),
      H:   parseRating(h.rating_H),
      N:   parseRating(h.rating_N),
      M:   parseRating(h.rating_M),
      K:   parseRating(h.rating_K),
      A:   parseRating(h.rating_A),
      Hnd: parseRating(h.rating_Hnd),
      total: parseRating(h.rating_total),
    },
  }));
};

function ManageRaces() {
  const navigate = useNavigate();
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRaceForm, setShowRaceForm] = useState(false);
  const [showHorsesForm, setShowHorsesForm] = useState(false);
  const [editingRace, setEditingRace] = useState(null);
  const [managingHorsesForRace, setManagingHorsesForRace] = useState(null);
  
  // Race Form state
  const [raceName, setRaceName] = useState('');
  const [venue, setVenue] = useState('');
  const [location, setLocation] = useState('');
  const [raceDate, setRaceDate] = useState('');
  const [raceType, setRaceType] = useState('thoroughbred');
  const [distance, setDistance] = useState('');
  const [pickDeadline, setPickDeadline] = useState('');
  const [status, setStatus] = useState('upcoming');

  // Horses Form state
  const [horses, setHorses] = useState([]);

  // Import / Export state
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadRaces();
  }, []);

  const loadRaces = async () => {
    try {
      setLoading(true);
      const response = await racesAPI.getRaces({ limit: 100 });
      if (response.success) {
        setRaces(response.data.races || []);
      }
    } catch (error) {
      console.error('Failed to load races:', error);
      alert('Failed to load races');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingRace(null);
    setRaceName('');
    setVenue('');
    setLocation('');
    setRaceDate('');
    setRaceType('thoroughbred');
    setDistance('');
    setPickDeadline('');
    setStatus('upcoming');
    setShowRaceForm(true);
    setShowHorsesForm(false);
  };

  const handleEditRace = (race) => {
    setEditingRace(race);
    setRaceName(race.raceName);
    setVenue(race.venue);
    setLocation(race.location);
    setRaceDate(race.raceDate.replace('Z', '').slice(0, 16));
    setRaceType(race.raceType || 'thoroughbred');
    setDistance(race.distance || '');
    setPickDeadline(race.pickDeadline.replace('Z', '').slice(0, 16));
    setStatus(race.status);
    setShowRaceForm(true);
    setShowHorsesForm(false);
  };

  const handleSubmitRace = async (e) => {
    e.preventDefault();
    
    if (!raceName.trim() || !venue.trim() || !location.trim() || !raceDate || !pickDeadline) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const raceData = {
        raceName: raceName.trim(),
        venue: venue.trim(),
        location: location.trim(),
        raceDate: new Date(raceDate).toISOString(),
        raceType,
        distance: distance.trim(),
        pickDeadline: new Date(pickDeadline).toISOString(),
        status
      };

      if (editingRace) {
        await racesAPI.updateRace(editingRace.raceId, raceData);
        alert('Race updated successfully!');
      } else {
        await racesAPI.createRace(raceData);
        alert('Race created successfully!');
      }

      setShowRaceForm(false);
      setEditingRace(null);
      loadRaces();
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to save race');
    }
  };

  const handleDeleteRace = async (raceId, raceName) => {
    if (!window.confirm(`Delete race "${raceName}"? This will also delete all associated picks!`)) {
      return;
    }

    try {
      await racesAPI.deleteRace(raceId);
      alert('Race deleted successfully!');
      loadRaces();
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to delete race');
    }
  };

  const handleManageHorses = (race) => {
    setManagingHorsesForRace(race);
    setHorses((race.horses || []).map(h => ({
      ...h,
      ratings: h.ratings && Object.keys(h.ratings).length ? h.ratings : DEFAULT_RATINGS(),
    })));
    setShowHorsesForm(true);
    setShowRaceForm(false);
  };

  const handleAddHorse = () => {
    setHorses([
      ...horses,
      {
        horseId: '',
        horseName: '',
        postPosition: horses.length + 1,
        jockey: '',
        trainer: '',
        scratched: false,
        odds: { decimal: '', fractional: '', american: '' },
        ratings: DEFAULT_RATINGS(),
      }
    ]);
  };

  const handleRemoveHorse = (index) => {
    setHorses(horses.filter((_, i) => i !== index));
  };

  const handleHorseChange = (index, field, value) => {
    const updatedHorses = [...horses];
    if (field.startsWith('odds.')) {
      const oddsField = field.split('.')[1];
      updatedHorses[index].odds[oddsField] = value;
    } else if (field.startsWith('ratings.')) {
      const ratingKey = field.split('.')[1];
      if (!updatedHorses[index].ratings) updatedHorses[index].ratings = DEFAULT_RATINGS();
      updatedHorses[index].ratings[ratingKey] = value;
    } else if (field === 'scratched') {
      updatedHorses[index].scratched = value;
    } else {
      updatedHorses[index][field] = value;
    }
    setHorses(updatedHorses);
  };

  const handleSubmitHorses = async (e) => {
    e.preventDefault();

    // Validate horses
    for (const horse of horses) {
      if (!horse.horseName.trim()) {
        alert('All horses must have a name');
        return;
      }
    }

    try {
      await racesAPI.updateHorses(managingHorsesForRace.raceId, { horses });
      alert('Horses updated successfully!');
      setShowHorsesForm(false);
      setManagingHorsesForRace(null);
      loadRaces();
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to update horses');
    }
  };

  const handleGenerateRatingsTidbit = async () => {
    const validHorses = horses.filter(h =>
      h.horseName &&
      RATING_CATEGORIES.some(c => parseFloat(h.ratings?.[c.key]) > 0)
    );
    if (validHorses.length === 0) {
      alert('No horses have ratings entered yet.');
      return;
    }
    const sorted = [...validHorses].sort((a, b) => (parseFloat(b.ratings?.total) || 0) - (parseFloat(a.ratings?.total) || 0));
    const headerCells = RATING_CATEGORIES.map(c =>
      `<th style="padding:0.4rem 0.6rem;text-align:center;border:1px solid #ccc;background:#2c2c2c;color:#fff" title="${c.label}">${c.key}</th>`
    ).join('');
    const rows = sorted.map((h, i) => {
      const cells = RATING_CATEGORIES.map(c =>
        `<td style="padding:0.4rem 0.6rem;text-align:center;border:1px solid #ccc">${h.ratings?.[c.key] !== '' && h.ratings?.[c.key] !== undefined ? h.ratings[c.key] : '-'}</td>`
      ).join('');
      const total = parseFloat(h.ratings?.total) || 0;
      const bg = i % 2 === 0 ? '#f8f8f8' : '#ffffff';
      return `<tr style="background:${bg}"><td style="padding:0.4rem 0.6rem;border:1px solid #ccc;font-weight:bold">${h.horseName}</td>${cells}<td style="padding:0.4rem 0.6rem;text-align:center;border:1px solid #ccc;font-weight:bold">${total}</td></tr>`;
    }).join('');
    const html = `<div style="overflow-x:auto"><table style="border-collapse:collapse;width:100%;font-size:0.9rem"><thead><tr><th style="padding:0.4rem 0.6rem;text-align:left;border:1px solid #ccc;background:#2c2c2c;color:#fff">Horse</th>${headerCells}<th style="padding:0.4rem 0.6rem;text-align:center;border:1px solid #ccc;background:#2c2c2c;color:#fff">Total</th></tr></thead><tbody>${rows}</tbody></table></div><p style="font-size:0.8rem;color:#888;margin-top:0.5rem">B=Breeding · H=Hype · N=Name · M=Mystery · K=Karma · A=Annoyance · Hnd=Handicapping</p>`;
    try {
      await tidbitsAPI.createTidbit({
        title: `${managingHorsesForRace.raceName} — Horse Ratings`,
        content: html,
        raceId: managingHorsesForRace.raceId,
      });
      alert('Ratings tidbit created! It will appear in the Race Information section on the race page.');
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to create ratings tidbit');
    }
  };

  const handleCancelRace = () => {
    setShowRaceForm(false);
    setEditingRace(null);
  };

  const handleCancelHorses = () => {
    setShowHorsesForm(false);
    setManagingHorsesForRace(null);
  };

  // ---- Export ----
  const handleExportRaces = async () => {
    try {
      const response = await racesAPI.exportRaces();
      const exportData = response.data;
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `races-export-${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to export races');
    }
  };

  const handleExportCSV = async () => {
    try {
      // Fetch latest full race data (with horses) from the export endpoint
      const response = await racesAPI.exportRaces();
      const exportData = response.data;
      const racesList = exportData.races || exportData;
      const rows = horsesToFlatRows(racesList);
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'RaceCard');
      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `races-export-${timestamp}.csv`);
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to export CSV');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await racesAPI.exportRaces();
      const exportData = response.data;
      const racesList = exportData.races || exportData;

      // Sheet 1 – race metadata
      const raceRows = racesList.map(race => ({
        raceId:       race.raceId || '',
        raceName:     race.raceName || '',
        venue:        race.venue || '',
        location:     race.location || '',
        raceDate:     race.raceDate || '',
        pickDeadline: race.pickDeadline || '',
        raceType:     race.raceType || '',
        distance:     race.distance || '',
        status:       race.status || '',
      }));

      // Sheet 2 – horses (one row per horse, keyed by raceId)
      const horseRows = [];
      racesList.forEach(race => {
        (race.horses || []).forEach(horse => {
          horseRows.push({
            raceId:         race.raceId || '',
            horseId:        horse.horseId || '',
            horseName:      horse.horseName || '',
            postPosition:   horse.postPosition ?? '',
            jockey:         horse.jockey || '',
            trainer:        horse.trainer || '',
            odds_decimal:   horse.odds?.decimal ?? '',
            odds_fractional: horse.odds?.fractional ?? '',
            odds_american:  horse.odds?.american ?? '',
            scratched:      horse.scratched ? 'true' : 'false',
            rating_B:       horse.ratings?.B ?? '',
            rating_H:       horse.ratings?.H ?? '',
            rating_N:       horse.ratings?.N ?? '',
            rating_M:       horse.ratings?.M ?? '',
            rating_K:       horse.ratings?.K ?? '',
            rating_A:       horse.ratings?.A ?? '',
            rating_Hnd:     horse.ratings?.Hnd ?? '',
            rating_total:   horse.ratings?.total ?? '',
          });
        });
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(raceRows), 'Races');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(horseRows.length ? horseRows : [{}]), 'Horses');
      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `races-export-${timestamp}.xlsx`);
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to export Excel');
    }
  };

  // ---- Import ----
  const handleImportClick = () => {
    setImportResult(null);
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'json') {
      // ---- JSON (existing behaviour) ----
      const reader = new FileReader();
      reader.onload = async (evt) => {
        let parsed;
        try {
          parsed = JSON.parse(evt.target.result);
        } catch {
          alert('Invalid JSON file. Please select a valid races export file.');
          return;
        }

        const racesArray = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed.races)
          ? parsed.races
          : null;

        if (!racesArray) {
          alert('File must contain a JSON object with a "races" array, or a bare array of race objects.');
          return;
        }

        await _doImport(racesArray, file.name);
      };
      reader.readAsText(file);

    } else if (ext === 'csv' || ext === 'xlsx' || ext === 'xls') {
      // ---- CSV / Excel ----
      const reader = new FileReader();
      reader.onload = async (evt) => {
        let racesArray;
        try {
          const data = new Uint8Array(evt.target.result);
          const wb = XLSX.read(data, { type: 'array' });

          if (ext === 'csv') {
            // Single flat sheet: one row per horse, race columns repeated
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
            racesArray = flatRowsToRaces(rows);

          } else {
            // Excel: preferred format is two sheets (Races + Horses)
            if (wb.SheetNames.includes('Races') && wb.SheetNames.includes('Horses')) {
              const raceRows  = XLSX.utils.sheet_to_json(wb.Sheets['Races'],  { defval: '' });
              const horseRows = XLSX.utils.sheet_to_json(wb.Sheets['Horses'], { defval: '' });
              racesArray = raceRows.map(r => {
                // Join key: prefer raceId; fall back to raceName so new races
                // without IDs still get their horses matched correctly.
                const raceKey = r.raceId || r.raceName;
                return {
                  ...(r.raceId ? { raceId: r.raceId } : {}),
                  raceName:     r.raceName,
                  venue:        r.venue,
                  location:     r.location,
                  raceDate:     r.raceDate,
                  pickDeadline: r.pickDeadline,
                  raceType:     r.raceType || 'thoroughbred',
                  distance:     r.distance || '',
                  status:       r.status || 'upcoming',
                  horses: horseRowsToHorses(
                    horseRows.filter(h => (h.raceId || h.raceName) === raceKey)
                  ),
                };
              });
            } else {
              // Fallback: treat first sheet as flat CSV format
              const ws = wb.Sheets[wb.SheetNames[0]];
              const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
              racesArray = flatRowsToRaces(rows);
            }
          }
        } catch (err) {
          alert('Failed to parse file: ' + err.message);
          return;
        }

        if (!racesArray || racesArray.length === 0) {
          alert('No races found in the file. Please check the format.');
          return;
        }

        await _doImport(racesArray, file.name);
      };
      reader.readAsArrayBuffer(file);

    } else {
      alert(`Unsupported file type ".${ext}". Please use .json, .csv, .xlsx, or .xls.`);
    }
  };

  const _doImport = async (racesArray, fileName) => {
    const confirmed = window.confirm(
      `Import ${racesArray.length} race(s) from "${fileName}"?\n\n` +
      `• Races with a matching raceId will be UPDATED.\n` +
      `• Races without a matching raceId will be CREATED.\n\n` +
      `Continue?`
    );
    if (!confirmed) return;

    try {
      setImportLoading(true);
      setImportResult(null);
      const response = await racesAPI.importRaces(racesArray);
      const result = response.data;
      setImportResult(result);
      loadRaces();
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to import races');
    } finally {
      setImportLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return <div className="loading">Loading races...</div>;
  }

  return (
    <div className="manage-races">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Manage Races</h2>
        <button onClick={() => navigate('/admin')} className="submit-button" style={{ background: '#666' }}>
          ← Back to Admin
        </button>
      </div>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.csv,.xlsx,.xls,application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />

      {!showRaceForm && !showHorsesForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button onClick={handleCreateNew} className="submit-button" style={{ flex: '1 1 160px' }}>
              + Create New Race
            </button>
            <button
              onClick={handleExportRaces}
              className="submit-button"
              style={{ flex: '1 1 160px', background: '#4caf50' }}
            >
              ⬇ Export JSON
            </button>
            <button
              onClick={handleExportCSV}
              className="submit-button"
              style={{ flex: '1 1 160px', background: '#388e3c' }}
            >
              ⬇ Export CSV
            </button>
            <button
              onClick={handleExportExcel}
              className="submit-button"
              style={{ flex: '1 1 160px', background: '#1b5e20' }}
            >
              ⬇ Export Excel
            </button>
            <button
              onClick={handleImportClick}
              className="submit-button"
              disabled={importLoading}
              style={{ flex: '1 1 160px', background: '#ff9800' }}
            >
              {importLoading ? 'Importing…' : '⬆ Import (JSON/CSV/Excel)'}
            </button>
          </div>

          {importResult && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              background: importResult.errors?.length ? '#fff3e0' : '#e8f5e9',
              border: `1px solid ${importResult.errors?.length ? '#ffb74d' : '#a5d6a7'}`
            }}>
              <strong>Import complete:</strong>{' '}
              {importResult.created} created, {importResult.updated} updated
              {importResult.errors?.length > 0 && (
                <details style={{ marginTop: '0.5rem' }}>
                  <summary style={{ cursor: 'pointer', color: '#e65100' }}>
                    {importResult.errors.length} error(s) — click to expand
                  </summary>
                  <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                    {importResult.errors.map((err, i) => (
                      <li key={i} style={{ fontSize: '0.85rem' }}>
                        Index {err.index}: {err.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {/* Race Form */}
      {showRaceForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>{editingRace ? 'Edit Race' : 'Create New Race'}</h3>
          <form onSubmit={handleSubmitRace}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Race Name *
              </label>
              <input
                type="text"
                value={raceName}
                onChange={(e) => setRaceName(e.target.value)}
                placeholder="e.g., Kentucky Derby 2026"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Venue *
                </label>
                <input
                  type="text"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  placeholder="e.g., Churchill Downs"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Location *
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., Louisville, KY"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Race Type
                </label>
                <select
                  value={raceType}
                  onChange={(e) => setRaceType(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="thoroughbred">Thoroughbred</option>
                  <option value="harness">Harness</option>
                  <option value="quarter_horse">Quarter Horse</option>
                  <option value="standardbred">Standardbred</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Distance
                </label>
                <input
                  type="text"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  placeholder="e.g., 1.25 miles"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Race Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={raceDate}
                  onChange={(e) => setRaceDate(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Pick Deadline *
                </label>
                <input
                  type="datetime-local"
                  value={pickDeadline}
                  onChange={(e) => setPickDeadline(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="submit-button" style={{ flex: 1 }}>
                {editingRace ? 'Update Race' : 'Create Race'}
              </button>
              <button type="button" onClick={handleCancelRace} className="submit-button" style={{ flex: 1, background: '#666' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Horses Management Form */}
      {showHorsesForm && managingHorsesForRace && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>Manage Horses - {managingHorsesForRace.raceName}</h3>
          <form onSubmit={handleSubmitHorses}>
            {horses.map((horse, index) => (
              <div key={index} style={{ 
                border: horse.scratched ? '2px solid #f44336' : '1px solid #ddd', 
                borderRadius: '8px', 
                padding: '1rem', 
                marginBottom: '1rem',
                background: horse.scratched ? '#fff8f8' : '#f9f9f9'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0 }}>Horse #{index + 1}</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      color: horse.scratched ? '#f44336' : '#555',
                      fontSize: '0.9rem'
                    }}>
                      <input
                        type="checkbox"
                        checked={!!horse.scratched}
                        onChange={(e) => handleHorseChange(index, 'scratched', e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      Scratched
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemoveHorse(index)}
                      style={{ 
                        background: '#f44336', 
                        color: 'white', 
                        border: 'none', 
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      Horse Name *
                    </label>
                    <input
                      type="text"
                      value={horse.horseName}
                      onChange={(e) => handleHorseChange(index, 'horseName', e.target.value)}
                      placeholder="e.g., Thunder Bolt"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                      required
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      Post Position
                    </label>
                    <input
                      type="number"
                      value={horse.postPosition}
                      onChange={(e) => handleHorseChange(index, 'postPosition', parseInt(e.target.value) || 0)}
                      min="0"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      Jockey
                    </label>
                    <input
                      type="text"
                      value={horse.jockey}
                      onChange={(e) => handleHorseChange(index, 'jockey', e.target.value)}
                      placeholder="e.g., Mike Smith"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      Trainer
                    </label>
                    <input
                      type="text"
                      value={horse.trainer}
                      onChange={(e) => handleHorseChange(index, 'trainer', e.target.value)}
                      placeholder="e.g., Bob Baffert"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #ddd', paddingTop: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
                    Odds
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                        Decimal (e.g., 5.5)
                      </label>
                      <input
                        type="text"
                        value={horse.odds.decimal}
                        onChange={(e) => handleHorseChange(index, 'odds.decimal', e.target.value)}
                        placeholder="5.5"
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                        Fractional (e.g., 9/2)
                      </label>
                      <input
                        type="text"
                        value={horse.odds.fractional}
                        onChange={(e) => handleHorseChange(index, 'odds.fractional', e.target.value)}
                        placeholder="9/2"
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                        American (e.g., +450)
                      </label>
                      <input
                        type="text"
                        value={horse.odds.american}
                        onChange={(e) => handleHorseChange(index, 'odds.american', e.target.value)}
                        placeholder="+450"
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Ratings */}
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Ratings (0–10) + Total</label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
                    {RATING_CATEGORIES.map(cat => (
                      <div key={cat.key}>
                        <label style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.75rem', color: '#555' }} title={cat.label}>
                          {cat.key}
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="1"
                          value={horse.ratings?.[cat.key] ?? ''}
                          onChange={(e) => handleHorseChange(index, `ratings.${cat.key}`, e.target.value)}
                          placeholder="-"
                          style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #ccc', textAlign: 'center' }}
                        />
                      </div>
                    ))}
                  </div>
                  {/* Total Field */}
                  <div style={{ marginTop: '0.75rem', borderTop: '1px solid #e0e0e0', paddingTop: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#1976d2' }}>
                      Total Rating (Manual Entry)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={horse.ratings?.total ?? ''}
                      onChange={(e) => handleHorseChange(index, 'ratings.total', e.target.value)}
                      placeholder="Enter total rating"
                      style={{ 
                        width: '150px',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '2px solid #1976d2',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        textAlign: 'center'
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddHorse}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: '0.5rem',
                fontSize: '1rem'
              }}
            >
              + Add Horse
            </button>

            <button
              type="button"
              onClick={handleGenerateRatingsTidbit}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#9c27b0',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: '1rem',
                fontSize: '1rem'
              }}
            >
              📊 Generate Ratings Tidbit
            </button>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="submit-button" style={{ flex: 1 }}>
                Save Horses & Odds
              </button>
              <button type="button" onClick={handleCancelHorses} className="submit-button" style={{ flex: 1, background: '#666' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Races List */}
      {!showRaceForm && !showHorsesForm && (
        <div>
          <h3>All Races ({races.length})</h3>
          {races.length === 0 ? (
            <div className="card">
              <p>No races found. Create your first race above!</p>
            </div>
          ) : (
            races.map((race) => (
              <div key={race.raceId} className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 0.5rem 0' }}>
                      {race.raceName}
                      <span style={{ 
                        marginLeft: '1rem',
                        padding: '0.25rem 0.75rem',
                        background: race.status === 'upcoming' ? '#4caf50' : race.status === 'completed' ? '#2196f3' : '#ff9800',
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 'normal'
                      }}>
                        {race.status}
                      </span>
                    </h3>
                    <p style={{ margin: '0.25rem 0', color: '#666' }}>
                      📍 {race.venue}, {race.location}
                    </p>
                    <p style={{ margin: '0.25rem 0', color: '#666' }}>
                      📅 Race: {formatDate(race.raceDate)}
                    </p>
                    <p style={{ margin: '0.25rem 0', color: '#666' }}>
                      ⏰ Pick Deadline: {formatDate(race.pickDeadline)}
                    </p>
                    {race.distance && (
                      <p style={{ margin: '0.25rem 0', color: '#666' }}>
                        📏 Distance: {race.distance}
                      </p>
                    )}
                    <p style={{ margin: '0.25rem 0', color: '#666' }}>
                      🐴 Horses: {race.horses?.length || 0}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '180px' }}>
                    <button
                      onClick={() => handleManageHorses(race)}
                      className="submit-button"
                      style={{ background: '#4caf50', padding: '0.5rem 1rem' }}
                    >
                      🐴 Manage Horses
                    </button>
                    <button
                      onClick={() => navigate(`/admin/races/${race.raceId}/results`)}
                      className="submit-button"
                      style={{ 
                        background: race.status === 'completed' ? '#9e9e9e' : '#ff9800', 
                        padding: '0.5rem 1rem' 
                      }}
                    >
                      {race.status === 'completed' ? '� Resubmit Results' : '🏁 Submit Results'}
                    </button>
                    <button
                      onClick={() => handleEditRace(race)}
                      className="submit-button"
                      style={{ background: '#2196f3', padding: '0.5rem 1rem' }}
                    >
                      ✏️ Edit Race
                    </button>
                    <button
                      onClick={() => handleDeleteRace(race.raceId, race.raceName)}
                      className="submit-button"
                      style={{ background: '#f44336', padding: '0.5rem 1rem' }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <style jsx="true">{`
        .manage-races {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
        }

        .card {
          background: white;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .submit-button {
          background: #007bff;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .submit-button:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .loading {
          text-align: center;
          padding: 2rem;
          font-size: 1.2rem;
        }
      `}</style>
    </div>
  );
}

export default ManageRaces;
